import React from "react";
import { Link } from "react-router-dom";
import Header from "./Header";
import Footer from "./Footer"
import Seo from "./Seo";

const sections = [
  { id: "about-signulu", label: "About Signulu" },
  { id: "signulus-role", label: "Signulu's role" },
  { id: "business-use-and-authority", label: "Business use and authority" },
  { id: "customer-responsibilities", label: "Customer responsibilities" },
  { id: "electronic-signature-validity", label: "Electronic-signature validity" },
  { id: "excluded-documents", label: "Excluded documents" },
  { id: "aadhaar-esign", label: "Aadhaar eSign" },
  { id: "customer-documents", label: "Customer documents" },
  { id: "fees-and-refunds", label: "Fees and refunds" },
  { id: "suspension", label: "Suspension" },
  { id: "no-legal-advice", label: "No legal advice" },
  { id: "limitation-of-liability", label: "Limitation of liability" },
  { id: "governing-law", label: "Governing law" },
  { id: "acceptable-use-policy", label: "Acceptable Use Policy" },
  { id: "refund-and-cancellation-policy", label: "Refund and Cancellation Policy" },
];

function TermsofServices() {

  return (
    <div className="terms-of-services">
      <Seo
        title="SignuluOne Terms of Service | Business Platform Terms"
        description="Read the terms governing Signulu websites, applications, APIs, electronic signing workflows, customer responsibilities, fees and acceptable use."
        canonical="https://www.signuluone.com/terms-of-service.html"
      />
      <Header />
      <main className="container container-lg container-xl container-xxl pricing_box">
        <h4>Terms of Service</h4>
        <div className="row toc-page-row">
          <aside className="col-lg-3 toc-sidebar">
            <div className="toc-sidebar-sticky">
              <p className="toc-sidebar-title">On this page</p>
              <ul>
                {sections.map((s) => (
                  <li key={s.id}><a href={`#${s.id}`}>{s.label}</a></li>
                ))}
              </ul>
            </div>
          </aside>

          <div className="col-lg-9 toc-page-content">
            <section id="about-signulu">
              <h4>1. About Signulu</h4>
              <p>Signulu is an electronic-signature and document-workflow platform provided by MITI Sphere Solutions Private Limited, a company incorporated under the Companies Act, 2013, having its registered office at MILLENNIUM Tower, 5th Floor, Plot# 10/1, Sector – III, HUDA Techno Enclave, Madhapur, Hyderabad, Telangana – 500081.</p>
              <p>These Terms govern the use of the Signulu website, applications, APIs and related services. By creating an account, accessing the Platform, sending a document or completing a transaction, you agree to these Terms.</p>
            </section>

            <section id="signulus-role">
              <h4>2. Signulu's role</h4>
              <p>Signulu provides technology for preparing, routing, approving, electronically signing, tracking and managing documents.</p>
              <p>Where Aadhaar-based eSign or another regulated digital-signature service is offered, Signulu facilitates the service through duly authorised service providers. Unless expressly stated otherwise, Signulu is not a Certifying Authority, Government authority, stamp-paper issuing authority, notary or legal adviser.</p>
            </section>

            <section id="business-use-and-authority">
              <h4>3. Business use and authority</h4>
              <p>Signulu may be used by organisations and their authorised employees, administrators, signers and other users for lawful business purposes.</p>
              <p>A person using Signulu on behalf of an organisation confirms that they are authorised to act for and bind that organisation.</p>
            </section>

            <section id="customer-responsibilities">
              <h4>4. Customer responsibilities</h4>
              <p>Customers are responsible for:</p>
              <ul>
                <li>uploading only lawful documents;</li>
                <li>obtaining the necessary rights and permissions to use recipient information;</li>
                <li>providing accurate signer and document details;</li>
                <li>ensuring that signatories have authority to sign;</li>
                <li>selecting an appropriate signing method;</li>
                <li>complying with stamp-duty, witnessing, notarisation and registration requirements;</li>
                <li>maintaining the confidentiality of account credentials; and</li>
                <li>preventing unauthorised access to their accounts.</li>
              </ul>
            </section>

            <section id="electronic-signature-validity">
              <h4>5. Electronic-signature validity</h4>
              <p>Electronic records, electronic contracts and prescribed electronic signatures are recognised under the Information Technology Act, 2000, subject to applicable requirements and exclusions.</p>
              <p>The legal validity and enforceability of a document depend on factors including the document type, selected signing method, authority of the signatories, intention of the parties, applicable stamp duty, registration requirements and other legal formalities.</p>
              <p>Signulu does not represent that every document can be lawfully executed using every available electronic-signature method. Customers should obtain independent legal advice where necessary.</p>
            </section>

            <section id="excluded-documents">
              <h4>6. Excluded documents</h4>
              <p>Unless permitted under an applicable statutory amendment, notification or other law, electronic execution under the Information Technology Act, 2000 may not apply to:</p>
              <ul>
                <li>negotiable instruments other than cheques;</li>
                <li>powers of attorney;</li>
                <li>trusts;</li>
                <li>wills and testamentary dispositions; and</li>
                <li>contracts for the sale or conveyance of immovable property or an interest in such property.</li>
              </ul>
              <p>Other documents may require witnesses, notarisation, stamping, registration or physical execution under applicable Central or State laws.</p>
            </section>

            <section id="aadhaar-esign">
              <h4>7. Aadhaar eSign</h4>
              <p>Aadhaar-based eSign, where available, is facilitated through an authorised eSign Service Provider and Certifying Authority.</p>
              <p>Authentication must be completed by the concerned signer. No Customer, Administrator or third party may enter an OTP, perform Aadhaar authentication or apply an Aadhaar eSign on behalf of another person.</p>
              <p>Signulu does not store Aadhaar OTPs, biometric information or private signing keys unless expressly disclosed and legally permitted.</p>
            </section>

            <section id="customer-documents">
              <h4>8. Customer documents</h4>
              <p>The Customer retains its rights in documents and content uploaded to Signulu.</p>
              <p>The Customer grants Signulu and its authorised service providers a limited right to host, process, transmit and store such content solely to provide the services, maintain security, comply with lawful instructions and satisfy applicable legal obligations.</p>
              <p>Signulu does not acquire ownership of Customer documents merely because they are processed through the Platform.</p>
            </section>

            <section id="fees-and-refunds">
              <h4>9. Fees and refunds</h4>
              <p>Subscription, implementation, transaction, eSign, eStamp, stamp duty and other charges shall be as stated in the applicable Order Form or pricing plan.</p>
              <p>Unless expressly agreed otherwise, fees are exclusive of GST, prepaid amounts and unused credits are non-refundable, and Government duties or third-party charges already incurred cannot be refunded.</p>
            </section>

            <section id="suspension">
              <h4>10. Suspension</h4>
              <p>Signulu may suspend an account or transaction due to non-payment, suspected fraud, unlawful activity, security threats, misuse, material breach or directions from a competent authority.</p>
              <p>Where reasonably practicable, Signulu will provide notice and an opportunity to remedy the issue.</p>
            </section>

            <section id="no-legal-advice">
              <h4>11. No legal advice</h4>
              <p>Signulu provides technology and does not draft, review or approve the legal contents of Customer documents. Signulu does not provide legal, tax, stamp-duty, registration or regulatory advice.</p>
              <p>Customers are responsible for determining the legal suitability and effectiveness of their documents and selected signing methods.</p>
            </section>

            <section id="limitation-of-liability">
              <h4>12. Limitation of liability</h4>
              <p>To the maximum extent permitted by law, Signulu's aggregate liability arising from the service shall not exceed the fees paid or payable for the affected service during the 12 months preceding the event giving rise to the claim.</p>
              <p>This limitation shall not apply to fraud, wilful misconduct, gross negligence, breach of confidentiality, intellectual-property infringement or liability that cannot lawfully be limited.</p>
            </section>

            <section id="governing-law">
              <h4>13. Governing law</h4>
              <p>These Terms shall be governed by the laws of India. Unresolved disputes shall be referred to arbitration in accordance with the Arbitration and Conciliation Act, 1996. The seat and venue of arbitration shall be Hyderabad, Telangana.</p>
              <p>Subject to the arbitration provision, courts at Hyderabad shall have jurisdiction.</p>
            </section>

            <section id="acceptable-use-policy">
              <h4>14. Acceptable Use Policy</h4>
              <p>Users must not use Signulu to:</p>
              <ul>
                <li>commit fraud or impersonate another person;</li>
                <li>forge or unlawfully alter documents;</li>
                <li>sign or authenticate on behalf of another person without lawful authority;</li>
                <li>obtain or use another person's OTP, PIN or signing credentials;</li>
                <li>send spam, phishing or unsolicited bulk communications;</li>
                <li>upload malware or harmful content;</li>
                <li>violate privacy, confidentiality or intellectual-property rights;</li>
                <li>process unlawfully obtained personal data;</li>
                <li>circumvent security controls or transaction limits;</li>
                <li>disrupt or test the Platform without permission;</li>
                <li>misrepresent Signulu as a Government body or Certifying Authority; or</li>
                <li>perform unlawful eSign or eStamp transactions.</li>
              </ul>
              <p>Signulu may suspend transactions or accounts that violate this policy and may cooperate with competent authorities where required by law.</p>
            </section>

            <section id="refund-and-cancellation-policy">
              <h4>15. Refund and Cancellation Policy</h4>
              <p>Subscription, implementation, user, transaction, eSign, eStamp and other charges shall be specified in the applicable pricing plan or Order Form.</p>
              <p>Unless expressly agreed otherwise:</p>
              <ul>
                <li>fees are exclusive of GST;</li>
                <li>subscription and implementation charges are non-refundable;</li>
                <li>unused transaction credits are non-refundable;</li>
                <li>unused advance balances are non-refundable;</li>
                <li>stamp duty and Government charges are non-refundable once procurement has commenced;</li>
                <li>third-party charges are non-refundable once incurred; and</li>
                <li>failed transactions caused by incorrect Customer or signer information do not qualify for refunds.</li>
              </ul>
              <p>If a transaction fails solely because of a verified Signulu system error and no statutory or third-party charge has been incurred, Signulu may restore the transaction credit or provide an appropriate adjustment.</p>
              <p>Cancellation does not remove payment obligations already committed or charges incurred before cancellation.</p>
            </section>

            <section id="terms-contact">
              <p>Questions about these terms? Write to <a href="mailto:legal@signulu.com">legal@signulu.com</a> or use our <Link to="/contact">contact page</Link>.</p>
            </section>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  )
}
export default TermsofServices;
